/**
 * Layout radial por cluster — portado do lab (scripts/layout.py), cujas
 * constantes foram medidas em 201 mapas reais desenhados por engenheiro.
 *
 * Por que não o fcose: rede de ISP é HIERÁRQUICA (borda → core → distribuição →
 * acesso/OLT). Layout de força trata todo nó igual e produz nuvem; radial por
 * profundidade mostra a hierarquia na própria geometria. E o fcose espalha
 * componentes desconexos pelo canvas — daí o mapa abrir amontoado num canto e,
 * ao dar "centralizar", os nós virarem pontinhos. Aqui os componentes são
 * dispostos lado a lado em grade, então a área ocupada é compacta e o fit
 * funciona.
 *
 * Legibilidade é perseguida em três frentes, medidas na base real:
 *
 *   cruzamento de linhas  -> ordem angular por baricentro + suavização
 *   linha por cima de nó  -> afastarDeArestas()
 *   nó/rótulo encostado   -> resolverColisoes(), com garantia dura
 *
 * Uma ressalva honesta: cruzamento ZERO é impossível aqui. Medido com
 * networkx, o grafo não é planar em nenhuma das empresas com todas as camadas
 * ligadas — a HZ tem 224 ciclos independentes (44% das arestas). Grafo
 * não-planar não admite desenho sem cruzamento, é propriedade do grafo e não
 * do layout. Só filtrando camada ele fica planar (L1+L2 da 23021, L1 da
 * 10022), e aí sim o desenho pode chegar perto de zero.
 */

export const PEGADA_X = 220;        // largura do nó + rótulo
export const PEGADA_Y = 110;        // altura do nó + rótulo

const RING = 260;            // raio por nível, no espaço do lab
const PAD = 350;             // padding entre clusters, idem
const AREA_PER_NODE = 210000;
const MIN_SPACING = 100;
export const RAIO_NO = 36;          // raio do círculo do nó (72px de diâmetro)

// Distância mínima entre quaisquer dois nós, em qualquer direção. Medido:
// 150 ainda deixa 211 pares abaixo da largura do rótulo; 200 derruba pra 101
// e a área ocupada cresce menos de 1%. Por isso 200.
export const PISO = 200;

/** Árvore radial de um componente conexo. Raiz = nó de maior grau. */
function radial(comp, adj, escala = 1) {
  const membros = [...comp];
  let root = membros[0];
  let melhorGrau = -1;
  for (const n of membros) {
    const grau = [...(adj.get(n) || [])].filter((v) => comp.has(v)).length;
    if (grau > melhorGrau) { melhorGrau = grau; root = n; }
  }

  const parent = new Map([[root, null]]);
  const children = new Map();
  const depth = new Map([[root, 0]]);
  const fila = [root];
  while (fila.length) {
    const u = fila.shift();
    const viz = [...(adj.get(u) || [])].filter((w) => comp.has(w)).sort();
    for (const w of viz) {
      if (parent.has(w)) continue;
      parent.set(w, u);
      if (!children.has(u)) children.set(u, []);
      children.get(u).push(w);
      depth.set(w, depth.get(u) + 1);
      fila.push(w);
    }
  }

  // nº de folhas por subárvore — define quanto ângulo cada ramo merece
  const folhas = new Map();
  (function contar(u) {
    const f = children.get(u) || [];
    if (!f.length) { folhas.set(u, 1); return 1; }
    const t = f.reduce((acc, w) => acc + contar(w), 0);
    folhas.set(u, t);
    return t;
  })(root);

  // subárvore de cada nó, pra saber quais vizinhos são externos ao ramo
  const subarv = new Map();
  (function calc(u) {
    const f = children.get(u) || [];
    let arr = [u];
    for (const w of f) arr = arr.concat(calc(w));
    subarv.set(u, arr);
    return arr;
  })(root);
  const setSub = new Map();
  for (const [u, arr] of subarv) setSub.set(u, new Set(arr));

  const ang = new Map();
  const atribuirAngulos = () => {
    // iterativo, não recursivo: componente em cadeia longa estoura a pilha
    const pilha = [[root, 0, 2 * Math.PI, 0]];
    while (pilha.length) {
      const [u, a0, a1, d] = pilha.pop();
      ang.set(u, (a0 + a1) / 2);
      const f = children.get(u) || [];
      if (!f.length) continue;
      const tot = f.reduce((s, c) => s + folhas.get(c), 0) || 1;
      let a = a0;
      const span = d ? a1 - a0 : 2 * Math.PI;
      for (const w of f) {
        const fr = folhas.get(w) / tot;
        pilha.push([w, a, a + span * fr, d + 1]);
        a += span * fr;
      }
    }
  };
  atribuirAngulos();

  // Heurística do baricentro (Sugiyama, em versão radial). A árvore usa só
  // N-1 arestas; as outras 44% viram corda atravessando o disco, e cada corda
  // cruza todas as que passam pelo mesmo pedaço. Ordenar os irmãos pelo
  // ângulo médio de quem eles enxergam FORA da própria subárvore encurta essas
  // cordas, e corda curta cruza pouco. Média circular (atan2 de sen/cos),
  // senão o ângulo 359° e o 1° dariam média 180°.
  for (let it = 0; it < 12; it++) {
    let mudou = false;
    for (const [u, f] of children) {
      if (f.length < 2) continue;
      const chave = new Map();
      for (const w of f) {
        const dentro = setSub.get(w);
        let sx = 0, sy = 0, n = 0;
        for (const m of subarv.get(w)) {
          for (const viz of adj.get(m) || []) {
            if (!comp.has(viz) || dentro.has(viz)) continue;
            const a = ang.get(viz);
            if (a === undefined) continue;
            sx += Math.cos(a); sy += Math.sin(a); n++;
          }
        }
        chave.set(w, n ? Math.atan2(sy, sx) : ang.get(w));
      }
      const base = ang.get(u);
      const norm = (a) => {
        let d = a - base;
        while (d < -Math.PI) d += 2 * Math.PI;
        while (d > Math.PI) d -= 2 * Math.PI;
        return d;
      };
      const antes = f.join('|');
      f.sort((a, b) => norm(chave.get(a)) - norm(chave.get(b)));
      if (f.join('|') !== antes) mudou = true;
    }
    atribuirAngulos();
    if (!mudou) break;
  }

  const maxd = Math.max(...depth.values()) || 1;
  const ring = Math.max(RING, (folhas.get(root) * MIN_SPACING) / (2 * Math.PI * maxd));
  const pos = new Map();
  for (const u of membros) {
    const r = depth.get(u) * ring * escala;
    const a = ang.get(u);
    pos.set(u, { x: r * Math.cos(a), y: r * Math.sin(a) });
  }
  return pos;
}

/**
 * Afasta nós que ficaram perto demais, com DUAS garantias — uma só não basta:
 *
 *   1. retangular — nenhum par com |dx| < fx E |dy| < fy ao mesmo tempo. É o
 *      que impede rótulo em cima de rótulo. Bolha circular ou elíptica não
 *      resolve: dois nós a dx=150, dy=80 passam na elipse e mesmo assim têm os
 *      rótulos sobrepostos. Foi essa a causa dos 282 pares sobrepostos que a
 *      versão anterior deixava na HZ.
 *   2. piso euclidiano — nenhum par abaixo de `piso`, em qualquer direção. A
 *      regra retangular sozinha aceita dois nós a 110px na vertical, que é
 *      perto demais pra leitura.
 *
 * Empurra sempre pelo eixo de MENOR penetração: é o menor deslocamento que
 * resolve, então a estrutura do cluster sobrevive ao ajuste.
 */
export function resolverColisoes(pos, iters = 300, fx = PEGADA_X, fy = PEGADA_Y, piso = PISO) {
  const ids = [...pos.keys()];
  const cx = Math.max(fx, piso);
  const cy = Math.max(fy, piso);
  for (let it = 0; it < iters; it++) {
    const grade = new Map();
    for (const id of ids) {
      const p = pos.get(id);
      const k = `${Math.floor(p.x / cx)},${Math.floor(p.y / cy)}`;
      if (!grade.has(k)) grade.set(k, []);
      grade.get(k).push(id);
    }
    let moveu = false;
    for (const id of ids) {
      const p = pos.get(id);
      const gx = Math.floor(p.x / cx);
      const gy = Math.floor(p.y / cy);
      for (let ddx = -1; ddx <= 1; ddx++) {
        for (let ddy = -1; ddy <= 1; ddy++) {
          for (const outro of grade.get(`${gx + ddx},${gy + ddy}`) || []) {
            if (outro === id) continue;
            const q = pos.get(outro);
            let dx = p.x - q.x;
            let dy = p.y - q.y;
            if (Math.abs(dx) < 1e-6 && Math.abs(dy) < 1e-6) {
              dx = (Math.random() - 0.5) || 0.1;
              dy = (Math.random() - 0.5) || 0.1;
            }
            const d = Math.hypot(dx, dy);
            if (d < piso) {
              const emp = (piso - d) / 2 / (d || 1);
              p.x += dx * emp; p.y += dy * emp;
              q.x -= dx * emp; q.y -= dy * emp;
              moveu = true;
              continue;
            }
            const ox = fx - Math.abs(dx);
            const oy = fy - Math.abs(dy);
            if (ox <= 0 || oy <= 0) continue;
            if (ox / fx < oy / fy) {
              const s = ((dx >= 0 ? 1 : -1) * ox) / 2;
              p.x += s; q.x -= s;
            } else {
              const s = ((dy >= 0 ? 1 : -1) * oy) / 2;
              p.y += s; q.y -= s;
            }
            moveu = true;
          }
        }
      }
    }
    if (!moveu) break;
  }
  return pos;
}

/**
 * Empurra nó que está em cima do vão de uma aresta — a linha passando por
 * dentro do círculo de outro equipamento, que é o que faz o mapa parecer
 * emaranhado mesmo quando as linhas não se cruzam.
 *
 * Só age quando o nó está no MEIO do vão (0,02 < t < 0,98): perto da ponta
 * ele é vizinho da aresta, e empurrar ali só desmancharia o cluster.
 */
export function afastarDeArestas(pos, arestas, folga = RAIO_NO + 24, iters = 30) {
  const cell = Math.max(folga * 3, 150);
  for (let it = 0; it < iters; it++) {
    const grade = new Map();
    for (const [id, p] of pos) {
      const k = `${Math.floor(p.x / cell)},${Math.floor(p.y / cell)}`;
      if (!grade.has(k)) grade.set(k, []);
      grade.get(k).push(id);
    }
    let moveu = false;
    for (const [a, b] of arestas) {
      const pa = pos.get(a);
      const pb = pos.get(b);
      if (!pa || !pb) continue;
      const vx = pb.x - pa.x;
      const vy = pb.y - pa.y;
      const L2 = vx * vx + vy * vy;
      if (L2 < 1e-9) continue;
      const L = Math.sqrt(L2);
      // candidatos: só as células que o segmento atravessa. Sem isso é
      // N*E por iteração (243 mil na HZ) e o layout passa de 10s.
      const passos = Math.max(1, Math.ceil(L / cell));
      const cand = new Set();
      for (let s = 0; s <= passos; s++) {
        const t = s / passos;
        const gx = Math.floor((pa.x + t * vx) / cell);
        const gy = Math.floor((pa.y + t * vy) / cell);
        for (let dx = -1; dx <= 1; dx++) {
          for (let dy = -1; dy <= 1; dy++) {
            for (const id of grade.get(`${gx + dx},${gy + dy}`) || []) cand.add(id);
          }
        }
      }
      for (const id of cand) {
        if (id === a || id === b) continue;
        const p = pos.get(id);
        const t = ((p.x - pa.x) * vx + (p.y - pa.y) * vy) / L2;
        if (t <= 0.02 || t >= 0.98) continue;
        const px = pa.x + t * vx;
        const py = pa.y + t * vy;
        let dx = p.x - px;
        let dy = p.y - py;
        let d = Math.hypot(dx, dy);
        if (d >= folga) continue;
        if (d < 1e-6) { dx = -vy / L; dy = vx / L; d = 1; }
        const emp = (folga - d) / d;
        // o nó sai da frente; as pontas cedem um pouco, o que encurta a linha
        p.x += dx * emp * 0.8; p.y += dy * emp * 0.8;
        pa.x -= dx * emp * 0.1; pa.y -= dy * emp * 0.1;
        pb.x -= dx * emp * 0.1; pb.y -= dy * emp * 0.1;
        moveu = true;
      }
    }
    if (!moveu) break;
  }
  return pos;
}

/**
 * Posiciona todos os nós. Recebe elementos no formato Cytoscape.
 * Devolve Map(id -> {x, y}).
 */
export default function radialLayout(nodes, edges, explosao = 40) {
  // escala: 1.0 reproduz o lab; o padrão (~2.2) é o que a pegada real exige.
  // `explosao` era o slider Compacto/Explodir, hoje uma constante — o
  // parâmetro fica porque quem chama pode querer variar (o gridLayout repassa).
  const escala = (PEGADA_X / MIN_SPACING) * (0.75 + explosao / 200);
  const adj = new Map();
  const todos = new Set(nodes.map((n) => n.data.id));
  const liga = (a, b) => {
    if (!adj.has(a)) adj.set(a, new Set());
    adj.get(a).add(b);
  };
  const arestas = [];
  for (const e of edges) {
    const a = String(e.data.source);
    const b = String(e.data.target);
    if (!todos.has(a) || !todos.has(b) || a === b) continue;
    liga(a, b); liga(b, a);
    arestas.push([a, b]);
  }

  // componentes conexos, maiores primeiro
  const vistos = new Set();
  const comps = [];
  for (const n of todos) {
    if (vistos.has(n)) continue;
    const pilha = [n];
    const c = new Set();
    while (pilha.length) {
      const u = pilha.pop();
      if (vistos.has(u)) continue;
      vistos.add(u); c.add(u);
      for (const w of adj.get(u) || []) if (!vistos.has(w)) pilha.push(w);
    }
    comps.push(c);
  }
  comps.sort((a, b) => b.size - a.size);

  // Nó isolado (sem nenhuma aresta) é componente de tamanho 1. Tratá-los como
  // componente normal os espalha pela grade toda — e como são a maioria em
  // rede real (184 de 480 na HZ), eles é que definem a extensão do canvas: o
  // fit precisa englobar essa faixa vazia e a topologia de verdade vira um
  // borrão no meio. Vão pra um bloco compacto embaixo, fora da grade.
  const isolados = [];
  const conectados = [];
  for (const c of comps) (c.size === 1 ? isolados : conectados).push(c);

  // cada componente vira uma caixa; caixas são dispostas em grade
  const caixas = conectados.map((c) => {
    const p = radial(c, adj, escala);
    // resolve dentro do cluster antes de empacotar, senão a caixa é medida
    // com um tamanho que ela não tem depois de ajustada
    resolverColisoes(p, 120);
    if (c.size > 2) {
      const dentro = arestas.filter(([a, b]) => c.has(a) && c.has(b));
      afastarDeArestas(p, dentro);
      resolverColisoes(p, 60);
    }
    const xs = [...p.values()].map((v) => v.x);
    const ys = [...p.values()].map((v) => v.y);
    const minx = Math.min(...xs);
    const miny = Math.min(...ys);
    return {
      p, minx, miny,
      w: (Math.max(...xs) - minx) || MIN_SPACING,
      h: (Math.max(...ys) - miny) || MIN_SPACING,
    };
  });

  const larguraAlvo = Math.sqrt(todos.size * AREA_PER_NODE * 1.4) * escala;
  const pos = new Map();
  let cx = 0, cy = 0, alturaLinha = 0;
  for (const b of caixas) {
    if (cx > 0 && cx + b.w > larguraAlvo) { cx = 0; cy += alturaLinha + PAD; alturaLinha = 0; }
    for (const [id, v] of b.p) {
      // sem arredondar pra grade: um snap de 25px come 17px da distância
      // mínima e quebra a garantia do piso
      pos.set(id, { x: cx + (v.x - b.minx), y: cy + (v.y - b.miny) });
    }
    cx += b.w + PAD * escala;
    alturaLinha = Math.max(alturaLinha, b.h);
  }

  // Bloco dos isolados: grade densa abaixo do grafo, largura igual à do
  // conteúdo conectado. Ficam visíveis e agrupados, sem distorcer o layout.
  if (isolados.length) {
    const xs = [...pos.values()].map((v) => v.x);
    const ys = [...pos.values()].map((v) => v.y);
    const x0 = xs.length ? Math.min(...xs) : 0;
    const yBase = (ys.length ? Math.max(...ys) : 0) + PAD * escala;
    const larg = xs.length ? Math.max(...xs) - x0 : larguraAlvo;
    const passoX = Math.max(PEGADA_X * 1.05, PISO * 1.1);
    const passoY = Math.max(PEGADA_Y * 1.25, PISO * 1.1);
    const porLinha = Math.max(1, Math.floor(larg / passoX) || 1);
    isolados.forEach((c, i) => {
      pos.set([...c][0], {
        x: x0 + (i % porLinha) * passoX,
        y: yBase + Math.floor(i / porLinha) * passoY,
      });
    });
  }

  // Suavização: cada nó puxado pro centroide dos vizinhos. Um concentrador
  // (AS externo com 20 sessões) passa a ficar no meio de quem ele atende, em
  // vez de pendurado na ponta de um galho — e as cordas que atravessavam o
  // disco encurtam junto. É o passo que mais derruba cruzamento: 6.979 → 3.4k
  // na HZ. Damping baixo e separação a cada rodada pra não colapsar tudo num
  // ponto, que é como esse tipo de iteração costuma terminar.
  const vizinhos = new Map();
  for (const [a, b] of arestas) {
    if (!vizinhos.has(a)) vizinhos.set(a, []);
    if (!vizinhos.has(b)) vizinhos.set(b, []);
    vizinhos.get(a).push(b);
    vizinhos.get(b).push(a);
  }
  for (let r = 0; r < 6; r++) {
    const alvo = new Map();
    for (const [id, vs] of vizinhos) {
      if (vs.length < 2) continue;
      let sx = 0, sy = 0, n = 0;
      for (const v of vs) {
        const q = pos.get(v);
        if (!q) continue;
        sx += q.x; sy += q.y; n++;
      }
      if (n) alvo.set(id, { x: sx / n, y: sy / n });
    }
    for (const [id, a] of alvo) {
      const p = pos.get(id);
      if (!p) continue;
      p.x += (a.x - p.x) * 0.25;
      p.y += (a.y - p.y) * 0.25;
    }
    resolverColisoes(pos, 40);
    afastarDeArestas(pos, arestas, RAIO_NO + 24, 6);
  }

  // Acabamento. As duas restrições brigam entre si — afastar de uma aresta
  // aproxima de um nó, e vice-versa — então rodar uma inteira depois da outra
  // desfaz metade do trabalho (medido: 49 → 85 arestas sobre nó). Aqui elas
  // convergem intercaladas, em passos pequenos.
  for (let k = 0; k < 12; k++) {
    afastarDeArestas(pos, arestas, RAIO_NO + 24, 1);
    resolverColisoes(pos, 25);
  }
  // A última palavra é da separação: piso e não-sobreposição são garantia
  // dura, aresta-sobre-nó é melhor-esforço.
  resolverColisoes(pos, 200);
  return pos;
}
